import cn from "classnames";
import { useEffect, useState, ChangeEvent } from "react";
import { Button, Input, Text } from "@rshbintech.dboul/core-ui";
import { makeSmsSignature } from "@rshbintech.dboul/utils";
import {
  monolithApi,
  confirmationApi,
  MonolithApiTypes,
  ConfirmationApiTypes,
} from "@rshbintech.dboul/http";
import { useAppTranslation } from "../../../hooks";
import { usePosition } from "../../position-context";
import { Base64 } from "../../utils";
import { Loader } from "../../loader";
import { SIGNING_WITH_SMS_STATUSES } from "../constants";
import { SmsConfirmationProps } from "./types";
import styles from "./styles.scss";

export const SmsConfirmation = ({
  buttonText,
  timer,
  setTimer,
  documentId,
  isTimerActive,
  onErrorSigning,
  onSuccessSigning,
  setIsTimerActive,
  setSigningStatus,
  cryptoControlBlock,
  virtualCertificate,
  signingWithMonolith,
  onClickSigningButton,
  fetchVirtualCertificate,
  isLoadingVirtualCertificate,
  signedDocument,
  attemptsCount,
  setAttemptsCount,
  dataAttributesSmsConfirmationButton,
}: SmsConfirmationProps) => {
  const [smsCode, setSmsCode] = useState("");
  const [invalidSmsCode, setInvalidSmsCode] = useState(false);
  const [signingProcessInProgress, setSigningProcessInProgress] =
    useState(false);

  const [isSmsCodeResendActive, setSmsCodeResendActive] = useState(false);
  const { translate } = useAppTranslation();

  const position = usePosition();

  useEffect(() => {
    if (
      attemptsCount === 0 &&
      !signingWithMonolith &&
      !isLoadingVirtualCertificate
    ) {
      onErrorSigning?.(translate("ERROR_LABEL_TEXT"));
      setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
    }
  }, [attemptsCount, signingWithMonolith, isLoadingVirtualCertificate]);

  useEffect(() => {
    let timerId: number;

    if (timer && isTimerActive) {
      timerId = window.setTimeout(setTimer, 1000, timer - 1);
    } else if (timer !== null && timer < 1 && isTimerActive) {
      setIsTimerActive(false);
      setSmsCodeResendActive(false);
    }

    return () => clearTimeout(timerId);
  }, [timer, isTimerActive]);

  const signDocument = async () => {
    if (!signingWithMonolith && documentId && signedDocument) {
      if (attemptsCount > 0) {
        try {
          const checkConfirmationResponse =
            await confirmationApi.api.checkConfirmationCode({
              documentId: String(documentId),
              documentType: signedDocument?.type.name,
              code: smsCode,
            });

          if (checkConfirmationResponse.data.valid) {
            setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
            onSuccessSigning?.();
          } else {
            setAttemptsCount((prev) => prev - 1);
            setInvalidSmsCode(true);
          }
        } catch (error) {
          setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
          onErrorSigning?.(
            (error as Record<string, ConfirmationApiTypes.ErrorMessage>)?.error
              ?.description
          ); // TODO: Изменить работу с типизацией и обработкой ошибок запросов
        }
      }
    } else if (
      signingWithMonolith &&
      virtualCertificate?.virtualCert &&
      virtualCertificate?.logUUID &&
      cryptoControlBlock
    ) {
      const {
        virtualCert: { certId, issuer, provider },
        logUUID,
      } = virtualCertificate;

      try {
        const otpCheckResponse =
          await monolithApi.otpCheckSign.otpCheckSignCreate({
            OTPSignLogUUID: logUUID,
            password: smsCode,
            action: "oneTimePswdSigning",
          });

        if (otpCheckResponse.data) {
          if (provider && certId && issuer && documentId) {
            setSigningProcessInProgress(true);
            const signature = makeSmsSignature([
              "1",
              new Date().getTime().toString(16),
              provider,
              certId,
              issuer,
              smsCode,
              logUUID,
              cryptoControlBlock,
            ]);

            await monolithApi.documents.signCreate({
              documentId: Number(documentId),
              certId,
              signature: Base64.encode(signature),
            });

            setSigningProcessInProgress(false);
            setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
            onSuccessSigning?.();
          }
        } else {
          setInvalidSmsCode(true);
          setSigningProcessInProgress(false);
        }
      } catch (error) {
        setSigningProcessInProgress(false);
        setSigningStatus(SIGNING_WITH_SMS_STATUSES.BEGIN_SIGNING);
        onErrorSigning?.(
          (error as Record<string, MonolithApiTypes.ErrorResponse>)?.error
            ?.errors?.[0]
        ); // TODO: Изменить работу с типизацией и обработкой ошибок запросов
      }
    }
  };

  const handleSignDocument = () => {
    signDocument();
    onClickSigningButton?.();
  };

  const handleClearSmsCode = () => {
    setSmsCode("");
  };

  const handleChangeSmsCode = (event: ChangeEvent<HTMLInputElement>) => {
    setSmsCode(event.target.value.replace(/\s/g, ""));
  };

  const handleSmsCodeResend = () => {
    fetchVirtualCertificate();
    setSmsCodeResendActive(true);
    setInvalidSmsCode(false);
  };

  const isDisabledSigningButton =
    timer === 0 || isLoadingVirtualCertificate || !smsCode.length;

  const timerClassNames = cn(styles.timer, styles[position], {
    [styles.error]: invalidSmsCode,
    [styles["active-timer"]]: isSmsCodeResendActive,
  });

  const requestSmsCodeLabelText = invalidSmsCode
    ? "REQUEST_CODE_FIRST_LABEL_ERROR_TEXT"
    : "REQUEST_CODE_LABEL_TEXT";

  return (
    <div className={cn(styles.wrapper, styles[position])}>
      {signingProcessInProgress || isLoadingVirtualCertificate ? (
        <Loader dataTestId="signing-with-sms-loader" />
      ) : (
        <div
          data-test-id="wrapper-signing-component"
          className={cn(styles["wrapper-signing-component"], styles[position])}
        >
          <Input
            type="text"
            value={smsCode}
            error={invalidSmsCode}
            label={translate("SMS_CODE_INPUT_LABEL") as string}
            onClear={handleClearSmsCode}
            onChange={handleChangeSmsCode}
            dataTestId="sms-code-input"
            className={cn(styles["sms-code-input"], styles[position])}
          />
          <div className={cn(styles["wrapper-timer"], styles[position])}>
            {isTimerActive && timer ? (
              <>
                <Text
                  size="xsmall"
                  className={timerClassNames}
                  dataTestId="timer-label"
                >
                  {translate(requestSmsCodeLabelText, { value: timer })}
                </Text>
                {invalidSmsCode && (
                  <Text
                    size="xsmall"
                    className={timerClassNames}
                    dataTestId="error-timer-label"
                  >
                    {translate("REQUEST_CODE_SECOND_LABEL_ERROR_TEXT")}
                  </Text>
                )}
              </>
            ) : !isSmsCodeResendActive ? (
              <div
                data-test-id="resend-button"
                onClick={handleSmsCodeResend}
                className={styles["resend-button"]}
              >
                <Text
                  size="small"
                  dataTestId="sms-code-resend-label"
                  className={styles["sms-code-resend-label"]}
                >
                  {translate("SMS_CODE_RESEND_LABEL_TEXT")}
                </Text>
              </div>
            ) : (
              <Loader
                className={styles["loader-for-resend-button"]}
                dataTestId="signing-with-sms-loader"
              />
            )}
          </div>
          <Button
            dataTestId="signing-button"
            onClick={handleSignDocument}
            disabled={isDisabledSigningButton}
            className={cn(styles["signing-button"], styles[position])}
            data-rptracker-event={
              dataAttributesSmsConfirmationButton?.dataRptrackerEvent
            }
            data-rptracker-category={
              dataAttributesSmsConfirmationButton?.dataRptrackerCategory
            }
            data-rptracker-action={
              dataAttributesSmsConfirmationButton?.dataRptrackerAction
            }
            data-rptracker-value={
              dataAttributesSmsConfirmationButton?.dataRptrackerValue
            }
          >
            {buttonText ?? translate("SIGNING_BUTTON_TEXT")}
          </Button>
        </div>
      )}
    </div>
  );
};
